import { useEffect, useRef, useState } from 'react';
import { Canvas } from './components/Canvas';
import { Inspector } from './components/Inspector';
import { Sidebar } from './components/Sidebar';
import { LeftToolDock } from './components/LeftToolDock';
import { Toolbar } from './components/Toolbar';
import { SheetTabs } from './components/SheetTabs';
import { StatusBar } from './components/StatusBar';
import { ToastHost, showToast } from './components/Toast';
import { WelcomeBanner } from './components/WelcomeBanner';
import { Landing } from './components/Landing';
import { MobileNotice } from './components/MobileNotice';
import { AuthScreen } from './components/AuthScreen';
import { ProjectsModal } from './components/ProjectsModal';
import { UpgradeModal } from './components/UpgradeModal';
import { ReadOnlyBanner } from './components/ReadOnlyBanner';
import { PasswordResetModal } from './components/PasswordResetModal';
import { NameModal } from './components/SheetModals';
import { saveCloudProject } from './cloudProjects';
import { activeSheet, useStore } from './store';
import { clearAutosave, readAutosave, writeAutosave, type AutosaveBlob } from './autosave';
import { buildSampleProject } from './sampleProject';
import { supabase, isSupabaseConfigured } from './supabase';
import { startLibraryCloudSync } from './cloudLibrary';
import { setEntitlementsFromSession } from './entitlements';
import type { Session } from '@supabase/supabase-js';

export default function App() {
  const setProject = useStore((s) => s.setProject);
  const theme = useStore((s) => s.theme);
  const addSubsystem = useStore((s) => s.addSubsystem);
  const activeSheetKind = useStore((s) => activeSheet(s).kind);
  const restoredRef = useRef(false);
  // Welcome-banner driven "add my first subsystem" modal — separate from the
  // identical modal in ProjectTree so the welcome flow doesn't depend on
  // the user finding the +. Same store action under the hood.
  const [welcomeSubsystemOpen, setWelcomeSubsystemOpen] = useState(false);
  // Pending autosave draft awaiting user decision (resume / start fresh).
  // Driven by a React modal instead of window.confirm() so that:
  //  (a) it behaves the same in browser and Electron (Electron's native
  //      confirm steals focus and can leave the WebContents in a state
  //      where typing into inputs doesn't register until you reload),
  //  (b) we never block the JS event loop during initial mount.
  const [pendingDraft, setPendingDraft] = useState<AutosaveBlob | null>(null);
  // Auth gate: login is required to reach the editor. When Supabase isn't
  // configured yet (no env vars) we fail OPEN — the app works without a gate —
  // so deploying this code before wiring keys can't lock everyone out. Once the
  // keys are set, the gate enforces sign-in.
  const [session, setSession] = useState<Session | null>(null);
  // Mirror of `session` for stable event listeners that must read the latest
  // value without re-subscribing (cloud/upgrade actions in demo mode).
  const sessionRef = useRef<Session | null>(null);
  const [authReady, setAuthReady] = useState(!isSupabaseConfigured);
  // Set when the user arrives via a password-reset email link (Supabase fires
  // PASSWORD_RECOVERY); shows the "set new password" modal over the app.
  const [recovering, setRecovering] = useState(false);
  // Cloud projects: which saved project (if any) the current canvas maps to, so
  // edits stream back to the right row. The ref mirrors the state so the
  // debounced autosave subscription below always reads the latest id without
  // re-subscribing.
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [cloudProjectId, setCloudProjectId] = useState<string | null>(null);
  const cloudProjectIdRef = useRef<string | null>(null);
  const setActiveCloudProject = (id: string | null) => {
    cloudProjectIdRef.current = id;
    setCloudProjectId(id);
    // A freshly opened/created cloud project supersedes any local resume draft.
    setPendingDraft(null);
  };
  useEffect(() => {
    if (!supabase) return;
    // A signed-in user has, by definition, passed the landing — keep them in
    // the editor across reloads (survives the OAuth URL cleanup too).
    const remember = (s: Session | null) => {
      setSession(s);
      sessionRef.current = s;
      setEntitlementsFromSession(s);
      if (s) {
        // A real session supersedes demo mode — drop the flag so a later
        // sign-out returns to the landing/auth rather than back into the demo.
        setDemoMode(false);
        try {
          sessionStorage.setItem('skema:entered', '1');
          sessionStorage.removeItem('skema:demo');
        } catch {
          // ignore
        }
        setEntered(true);
      }
    };
    supabase.auth.getSession().then(({ data }) => {
      remember(data.session);
      setAuthReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      remember(s);
      if (event === 'PASSWORD_RECOVERY') setRecovering(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);
  // Landing gate: every fresh visit (including returning users and crawlers)
  // lands on the marketing page — it doubles as the future signed-out home
  // once accounts/Pro arrive. Clicking "Open" enters the editor for the rest
  // of the tab session (sessionStorage, so an accidental reload mid-work
  // doesn't bounce you back out). #app deep links and the packaged desktop
  // app (which loads via file://) skip straight to the editor — a marketing
  // page makes no sense inside the installed shell.
  const [entered, setEntered] = useState(() => {
    try {
      if (window.location.hash.includes('app')) return true;
      // OAuth callback (Supabase puts the token in the URL hash) — land the
      // user straight in the editor rather than bouncing back to the landing.
      if (window.location.hash.includes('access_token')) return true;
      if (window.location.protocol === 'file:') return true;
      if (sessionStorage.getItem('skema:entered') === '1') return true;
    } catch {
      // ignore
    }
    return false;
  });
  // Demo mode: the "Example project" path lets a signed-out visitor explore the
  // editor without the login wall (local only — cloud save / account actions
  // route them to sign-in). Persisted for the tab so a reload doesn't bounce
  // them to the auth screen mid-explore.
  const [demoMode, setDemoMode] = useState(() => {
    try {
      return sessionStorage.getItem('skema:demo') === '1';
    } catch {
      return false;
    }
  });
  /** Leave the demo and surface the sign-in screen (entered stays true, so the
   *  auth gate below renders AuthScreen). Used when a demo visitor hits an
   *  action that genuinely needs an account. */
  const goSignIn = () => {
    setDemoMode(false);
    try {
      sessionStorage.removeItem('skema:demo');
    } catch {
      // ignore
    }
  };
  const launch = (resume = false) => {
    try {
      sessionStorage.setItem('skema:entered', '1');
    } catch {
      // ignore
    }
    // "Continue where you left off" restores the draft immediately — no
    // second confirmation inside the app. Plain "Open" keeps the resume
    // prompt so a fresh start is still one click away.
    if (resume) onResume();
    setEntered(true);
  };

  // Load the demo project and drop straight into the editor. Shared by the
  // landing "Explore the example" CTA and the in-app welcome banner. Clears
  // any pending autosave prompt so the resume modal doesn't pop over the
  // freshly loaded example.
  const loadExample = (enterApp: boolean) => {
    // Detach any active cloud project first so the example's contents never
    // stream over the user's saved cloud row.
    setActiveCloudProject(null);
    setProject(buildSampleProject());
    setPendingDraft(null);
    try {
      localStorage.setItem('skema:welcome-dismissed', '1');
    } catch {
      // ignore
    }
    if (enterApp) {
      // Let a signed-out visitor into the editor without the login wall. Once
      // signed in they don't need it (the gate passes on session alone).
      if (isSupabaseConfigured && !sessionRef.current) {
        setDemoMode(true);
        try {
          sessionStorage.setItem('skema:demo', '1');
        } catch {
          // ignore
        }
      }
      try {
        sessionStorage.setItem('skema:entered', '1');
      } catch {
        // ignore
      }
      setEntered(true);
    }
    showToast('Loaded the example project — explore and edit freely', 'info');
  };

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // On first mount, look for an autosave draft and offer to restore it.
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const draft = readAutosave();
    if (!draft) return;
    const totalNodes = draft.sheets.reduce((s, sh) => s + sh.nodes.length, 0);
    const totalEdges = draft.sheets.reduce((s, sh) => s + sh.edges.length, 0);
    if (totalNodes === 0 && totalEdges === 0) {
      clearAutosave();
      return;
    }
    setPendingDraft(draft);
  }, []);

  const onResume = () => {
    if (!pendingDraft) return;
    setProject({
      meta: pendingDraft.meta,
      sheets: pendingDraft.sheets,
      activeSheetId: pendingDraft.activeSheetId,
    });
    setPendingDraft(null);
    showToast('Resumed autosaved draft', 'info');
  };

  const onDiscard = () => {
    clearAutosave();
    setPendingDraft(null);
  };

  // Debounced autosave whenever the document state changes.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsub = useStore.subscribe((state) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        writeAutosave(state.meta, state.sheets, state.activeSheetId);
      }, 800);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, []);

  // Cloud autosave: when a cloud project is active, stream edits back to its
  // row (data only — the name is owned by the projects list). Debounced longer
  // than the local autosave so we don't hammer the network on every keystroke.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsub = useStore.subscribe((state) => {
      if (!cloudProjectIdRef.current) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        // Re-read the id when the debounce fires, not when it was scheduled: if
        // the project was detached in between (New / Open / Example loaded a
        // different project), this must NOT overwrite the old cloud row with
        // the new project's contents. That detach is what fixes the data loss.
        const id = cloudProjectIdRef.current;
        if (!id) return;
        void saveCloudProject(id, {
          meta: state.meta,
          sheets: state.sheets,
          activeSheetId: state.activeSheetId,
        }).catch(() => showToast('Cloud save failed — your work is saved locally', 'error'));
      }, 1500);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, []);

  // AccountMenu / project tree → "My projects" opens the cloud browser. A demo
  // visitor (no session) has no account to list, so send them to sign-in first.
  useEffect(() => {
    const open = () => {
      if (isSupabaseConfigured && !sessionRef.current) {
        goSignIn();
        return;
      }
      setProjectsOpen(true);
    };
    window.addEventListener('skema:open-projects', open);
    return () => window.removeEventListener('skema:open-projects', open);
  }, []);

  // "Upgrade to Pro" (account menu, or a free-plan limit) opens the paywall.
  // Checkout needs a user id to attribute the purchase, so a demo visitor signs
  // in first.
  useEffect(() => {
    const open = () => {
      if (isSupabaseConfigured && !sessionRef.current) {
        goSignIn();
        return;
      }
      setUpgradeOpen(true);
    };
    window.addEventListener('skema:open-upgrade', open);
    return () => window.removeEventListener('skema:open-upgrade', open);
  }, []);

  // Component-library cloud sync: while signed in, pull+merge the account's
  // library once, then push (debounced) every local library edit. Fully
  // silent — offline or unconfigured it's a no-op and the library stays local.
  useEffect(() => {
    if (!session) return;
    return startLibraryCloudSync();
  }, [session]);

  // Clicking the logo in the editor returns to the marketing landing. Work is
  // preserved (store + localStorage autosave), so re-entering resumes it.
  // Also clear the session flag so a reload stays on the landing instead of
  // bouncing back into the editor.
  useEffect(() => {
    const goHome = () => {
      try {
        sessionStorage.removeItem('skema:entered');
      } catch {
        // ignore
      }
      setEntered(false);
    };
    window.addEventListener('skema:go-home', goHome);
    return () => window.removeEventListener('skema:go-home', goHome);
  }, []);

  // Starting a different project (New / Open a file) must sever the link to the
  // active cloud row, or the cloud autosave would overwrite that saved project
  // with the new (often empty) contents. Fired by the project actions.
  useEffect(() => {
    const detach = () => setActiveCloudProject(null);
    window.addEventListener('skema:cloud-detach', detach);
    return () => window.removeEventListener('skema:cloud-detach', detach);
  }, []);

  // Returning from the Lemon Squeezy checkout (?upgraded=1). The webhook flips
  // app_metadata.plan server-side; the browser only sees it after a token
  // refresh, and there's a small race until the webhook lands — so poll a few
  // refreshes until Pro shows up. onAuthStateChange('TOKEN_REFRESHED') then
  // updates entitlements automatically.
  useEffect(() => {
    if (!supabase) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgraded') !== '1') return;
    params.delete('upgraded');
    const clean = window.location.pathname + (params.toString() ? `?${params}` : '') + window.location.hash;
    window.history.replaceState(null, '', clean);

    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      const { data } = await supabase!.auth.refreshSession();
      const plan = (data.session?.user?.app_metadata as { plan?: string } | undefined)?.plan;
      if (cancelled) return;
      if (plan === 'pro') {
        showToast('Welcome to Pro — everything is unlocked 🎉', 'success', 3500);
      } else if (tries < 4) {
        setTimeout(() => void tick(), 2500);
      } else {
        showToast('Payment received — Pro will activate shortly. Try reloading in a minute.', 'info', 4000);
      }
    };
    void tick();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!entered)
    return (
      <Landing
        onLaunch={launch}
        onOpenExample={() => loadExample(true)}
        hasDraft={pendingDraft !== null}
      />
    );

  // Login wall (only when Supabase is configured). Wait for the initial
  // session check so we don't flash the sign-in screen for an already
  // logged-in user on reload.
  if (isSupabaseConfigured && !demoMode) {
    if (!authReady) return <div style={{ height: '100vh', background: 'var(--bg)' }} />;
    if (!session) return <AuthScreen onBack={() => setEntered(false)} />;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        background: 'var(--bg)',
      }}
    >
      <Toolbar />
      {isSupabaseConfigured && demoMode && !session && <DemoBanner onSignIn={goSignIn} />}
      <SheetTabs />
      <ReadOnlyBanner />
      <div
        style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}
      >
        <Sidebar />
        <LeftToolDock />
        <Canvas />
        {activeSheetKind !== 'interconnect' && <Inspector />}
        <WelcomeBanner
          onAddSubsystem={() => setWelcomeSubsystemOpen(true)}
          onLoadExample={() => loadExample(false)}
          onOpenGettingStarted={() => {
            // Toolbar listens for this event and opens the help modal on the
            // Getting Started tab. Decoupling via event keeps Toolbar's local
            // state encapsulated.
            window.dispatchEvent(
              new CustomEvent('skema:open-help', {
                detail: { tab: 'gettingStarted' },
              }),
            );
          }}
        />
      </div>
      <StatusBar />
      <ToastHost />
      <MobileNotice />
      {projectsOpen && (
        <ProjectsModal
          onClose={() => setProjectsOpen(false)}
          currentCloudProjectId={cloudProjectId}
          onActiveProjectChange={setActiveCloudProject}
        />
      )}
      {upgradeOpen && <UpgradeModal onClose={() => setUpgradeOpen(false)} />}
      {recovering && <PasswordResetModal onDone={() => setRecovering(false)} />}
      {pendingDraft && (
        <ResumeModal draft={pendingDraft} onResume={onResume} onDiscard={onDiscard} />
      )}
      {welcomeSubsystemOpen && (
        <NameModal
          title="Add your first subsystem"
          description={
            'A subsystem represents one physical box in your machine — typical examples:\n  • Robot\n  • Control Box\n  • Sensor Pod\n\nSkematiq will create three sheets for it (Requirements + Block diagram + Schematic) all under a new folder in the project tree.'
          }
          defaultValue="Robot"
          okLabel="Add subsystem"
          onCancel={() => setWelcomeSubsystemOpen(false)}
          onSubmit={(name) => {
            addSubsystem(name);
            setWelcomeSubsystemOpen(false);
            // Mark welcome as dismissed so it doesn't pop back.
            try {
              localStorage.setItem('skema:welcome-dismissed', '1');
            } catch {
              // ignore
            }
          }}
        />
      )}
    </div>
  );
}

/** Slim strip shown while a signed-out visitor explores the example, nudging
 *  them to sign in (which is also how they'd save to the cloud or upgrade). */
function DemoBanner({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '6px 14px',
        background: 'var(--accent-soft)',
        borderBottom: '1px solid var(--accent-border)',
        color: 'var(--text)',
        fontSize: 12.5,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        You're exploring the example — <strong>sign in</strong> to save your work and use cloud projects.
      </span>
      <button
        onClick={onSignIn}
        style={{
          flexShrink: 0,
          border: '1px solid var(--accent)',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--accent)',
          color: '#fff',
          fontSize: 12,
          fontWeight: 700,
          padding: '4px 12px',
          cursor: 'pointer',
        }}
      >
        Sign in / Sign up
      </button>
    </div>
  );
}

function ResumeModal({
  draft,
  onResume,
  onDiscard,
}: {
  draft: AutosaveBlob;
  onResume: () => void;
  onDiscard: () => void;
}) {
  const totalNodes = draft.sheets.reduce((s, sh) => s + sh.nodes.length, 0);
  const totalEdges = draft.sheets.reduce((s, sh) => s + sh.edges.length, 0);
  const ts = new Date(draft.savedAt).toLocaleString();
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: 'var(--panel)',
          color: 'var(--text)',
          border: '1px solid var(--panel-border-strong)',
          borderTop: '2px solid var(--accent)',
          borderRadius: 'var(--radius)',
          padding: 20,
          minWidth: 420,
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
          Resume your previous work?
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-muted)',
            lineHeight: 1.6,
            marginBottom: 16,
          }}
        >
          <div>
            Autosaved <strong>{ts}</strong>
          </div>
          <div>
            {totalNodes} nodes, {totalEdges} wires across {draft.sheets.length} sheets
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onDiscard}
            style={{
              background: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--panel-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '6px 14px',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Start fresh
          </button>
          <button
            autoFocus
            onClick={onResume}
            style={{
              background: 'var(--accent)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              padding: '6px 16px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Resume
          </button>
        </div>
      </div>
    </div>
  );
}
